import { useEffect, useState } from 'react'
import NameIcon from './shared/NameIcon'
import Spinner from 'app/components/Spinner'
import { gqlFetchList } from 'app/lib/graphqlClient'
import { bg } from 'lib/background'
import { logWarn } from 'lib/logging'

let fetching: Record<string, Promise<string>> = {}
let cache: Record<string, string> = {}

const DELETED_CONTACT = `Deleted Contact`

/**
 * Fetches a contact's display name through GraphQL and caches it at module scope so
 * repeated lookups for the same id are free. Returns `Deleted Contact` for misses and
 * errors; callers should treat that string as the soft-deleted/missing state.
 */
export async function getName(contactId: string): Promise<string | undefined> {
  if (cache[contactId] != null) return cache[contactId]
  if (fetching[contactId] != null) return fetching[contactId]
  if (!contactId || contactId === `undefined`) return
  // The single-id `contact(id)` resolver currently throws INTERNAL_SERVER_ERROR
  // for many contacts that actually exist — same pattern as the `user.role` bug.
  // Sidestep by querying the contacts list with an id filter; the list resolver
  // doesn't have the same problem.
  fetching[contactId] = (async () => {
    try {
      const { hits } = await gqlFetchList<{ id: string; name?: string }>(
        `contacts`,
        { search: `id:${contactId}`, pagination: { page: 0, limit: 1 } },
        `id name`,
      )
      const contact = hits?.[0]
      if (!contact) return (cache[contactId] = DELETED_CONTACT)
      return (cache[contactId] = contact.name || `Unknown`)
    } catch (err) {
      logWarn(`contact(${contactId}) fetch threw`, err)
      return (cache[contactId] = DELETED_CONTACT)
    }
  })()
  return fetching[contactId]
}

export function LazyContactCell(props: { contactId: string; loadingIndicator?: boolean }) {
  const [name, setName] = useState<string>(cache[props.contactId])

  useEffect(() => {
    if (name && cache[props.contactId] === name) return
    else setName(``)
    bg(
      getName(props.contactId).then((fetched) => {
        if (fetched) setName(fetched)
      }),
    )
  }, [props.contactId])

  if (name === DELETED_CONTACT) {
    return (
      <div className="flex items-center gap-x-2 p-2">
        <span className="italic text-subtle">{DELETED_CONTACT}</span>
      </div>
    )
  }

  return name ? (
    <div className="flex items-center gap-x-2 p-2">
      <NameIcon fullName={name[0]} heightClassName="h-5" widthClassName="w-5" isTiny />
      <span>{name}</span>
    </div>
  ) : props.loadingIndicator !== false ? (
    <Spinner size={5} />
  ) : null
}
